import React, { useState, useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Theme from '../common/NekoTheme';
import { NekoIcon } from '../misc/Icon';
import { useClasses } from '../hooks';

const arrayToString = (array) => {
  if (!Array.isArray(array)) {
    console.warn('The provided value is not an array. Falling back to an empty array.');
    array = [];
  }
  return array.join(', ');
};

const stringToArray = (str) => str.split(',').map(item => item.trim()).filter(item => item.length > 0);

const arraysAreEqual = (arr1, arr2) => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2) || arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
};

/**
 * @param {string} id
 * @param {string} name
 * @param {string} value
 * @param {string} description
 * @param {string} placeholder
 */
const ForwardedNekoInput = (props, ref) => {
  const { name, value = '', description, placeholder = '', onEnter = null, isCommaSeparatedArray = false,
  readOnly = false, type = 'text', step = 1, min = 1, max = 999, maxLength : defaultMaxLength = undefined,
  className, style, natural = false, inputStyle, onChange: onCustomChange, onBlur: onCustomBlur, onReset, ...rest } = props;

  const [ newValue, setNewValue ] = useState(value);
  const isControlled = !!onCustomChange;
  const maxLength = defaultMaxLength ? defaultMaxLength : (type === 'number' ? 3 : undefined);

  useEffect(() => {
    if (!isControlled) {
      setNewValue(isCommaSeparatedArray ? arrayToString(value) : value);
    }
  }, [value]);

  const onChange = (e) => {
    const targetValue = e.target.value;
    const newValue = isCommaSeparatedArray ? stringToArray(targetValue) : targetValue;

    e.stopPropagation();
    e.preventDefault();
    
    if (isControlled) {
      onCustomChange(newValue, name);
    } else {
      setNewValue(targetValue);
    }
  };

  const onKeyPress = (e) => {
    if (onEnter && e.key === 'Enter') {
      e.preventDefault();
      const targetValue = e.target.value;
      const newValue = isCommaSeparatedArray ? stringToArray(targetValue) : targetValue;
      onEnter(newValue, name);
    }
  };

  const onBlur = (e) => {
    if (onCustomBlur) {
      const targetValue = e.target.value;
      const newValue = isCommaSeparatedArray ? stringToArray(targetValue) : targetValue;
  
      const valuesAreEqual = isCommaSeparatedArray
        ? arraysAreEqual(value, newValue)
        : value === newValue;
  
      if (!valuesAreEqual) {
        onCustomBlur(newValue, name);
      }
    }
  };

  const classNames = useClasses('neko-input', { natural });

  return (
    <div className={className} style={style}>
      <div style={{ position: 'relative' }}>
        {type === 'number' ? <input ref={ref} className={classNames} name={name}
            value={isControlled ? value : newValue} type={type}
            disabled={readOnly}
            step={step} min={min} max={max} maxLength={maxLength}
            autoComplete="off" data-form-type="other"
            placeholder={placeholder} style={inputStyle}
            onChange={onChange} onKeyPress={onKeyPress} onBlur={onBlur}
            readOnly={readOnly} {...rest}
          /> : <input ref={ref} className={classNames} {...rest} name={name}
            value={isControlled ? value : newValue} type={type}
            disabled={readOnly}
            spellCheck="false"
            autoComplete="off" data-form-type="other"
            placeholder={placeholder} style={inputStyle} maxLength={maxLength}
            onChange={onChange} onKeyPress={onKeyPress} onBlur={onBlur}
            readOnly={readOnly} {...rest}
          />
        }
        {!!value && !!onReset && <NekoIcon icon='close' width={24} style={{ position: 'absolute', top: '3px', right: '3px' }} variant='blue' onClick={() => onReset()} />}
      </div>
      {description && <p className="neko-input-description">{description}</p>}
    </div>
  );
};

const StyledNekoInput = Styled(forwardRef(ForwardedNekoInput))`
  .neko-input {
    font-family: ${Theme.fontFamily};
    font-size: ${Theme.fontSizeText};
    border: 1.5px solid var(--neko-input-border);
    box-sizing: border-box;
    height: 30px;
    background: var(--neko-input-background);
    color: var(--neko-input-color);
    padding: 0 10px;
    width: 100%;
    transition: background 0.3s ease, color 0.3s ease;

    &.natural {
      border-color: gray;
      border-width: 1px;
    }

    &::placeholder {
      color: rgba(0, 0, 0, 0.25);
    }

    :focus {
      background-color: white;
    }

    :read-only {
      background-color: var(--neko-gray);
    }

    :disabled {
      color: var(--neko-dark-gray);
      border: none;
      box-shadow: none;
    }
  }

  .neko-input-description {
    font-family: ${Theme.fontFamily};
    font-size: ${Theme.fontSizeSmall};
    color: var(--neko-dark-gray);
    line-height: 14px;
    margin-top: 5px;
    margin-bottom: 0;
  }
`;

const NekoInput = React.forwardRef((props, ref) => {
  return (<StyledNekoInput ref={ref} {...props} />);
});

NekoInput.propTypes = {
  type: PropTypes.oneOf(['number', 'text']),
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  description: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onEnter: PropTypes.func,
  onBlur: PropTypes.func,
  readOnly: PropTypes.bool,
  step: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  natural: PropTypes.bool,
  onReset: PropTypes.func,
  isCommaSeparatedArray: PropTypes.bool,
};

NekoInput.defaultProps = {
  type: 'text',
  name: undefined,
  value: '',
  description: undefined,
  placeholder: '',
  onChange: undefined,
  onEnter: undefined,
  onBlur: undefined,
  readOnly: false,
  step: 1,
  min: 1,
  max: 999,
  maxLength: undefined,
  natural: false,
  onReset: undefined,
  isCommaSeparatedArray: false,
};

export { NekoInput };
